'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { profileScrollBehavior, profileScrollIntent } from './profile-scroll-intent';

const getServerSnapshot = () => null;

/** Call only after a successful save; normal navigation retains Next's behavior. */
export function useProfileNavigation() {
  const router = useRouter();
  return useCallback(
    (href: string) => {
      profileScrollIntent.request(window.location.href, href);
      try {
        router.push(href, { scroll: false });
      } catch (error) {
        profileScrollIntent.cancel();
        throw error;
      }
    },
    [router],
  );
}

/** Keep this mounted in AppProvider so abandoned saves cannot scroll a later visit. */
export function ProfileNavigationObserver() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const query = searchParams.toString();

  useEffect(() => {
    profileScrollIntent.observe(window.location.href);
  }, [pathname, query]);

  useEffect(() => {
    const cancel = () => profileScrollIntent.cancel();
    window.addEventListener('popstate', cancel);
    return () => window.removeEventListener('popstate', cancel);
  }, []);

  return null;
}

/** Mount within the ready profile subtree, after its requested panel is selected. */
export function ProfileScrollController({ currentStep }: { currentStep?: string }) {
  const intent = useSyncExternalStore(
    profileScrollIntent.subscribe,
    profileScrollIntent.getSnapshot,
    getServerSnapshot,
  );
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const query = searchParams.toString();

  useEffect(() => {
    if (!intent || !currentStep) return;
    // Wait for this committed profile panel, not a pathname update in the persistent header.
    const frame = window.requestAnimationFrame(() => {
      if (!profileScrollIntent.consume(window.location.href, currentStep, intent)) return;
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: profileScrollBehavior(
          window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        ),
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [intent, currentStep, pathname, query]);

  return null;
}
